import {
  StripePayoutSummaryItemType,
  StripeTransactionItem,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { paymentsSentry } from '@frontend/payments-hooks';

/**
 * Categorizes a transaction type into its summary bucket.
 * Maps transaction types to their corresponding summary item type.
 */
function getTransactionBucketType(transactionType?: string): StripePayoutSummaryItemType | null {
  if (!transactionType) return null;

  const type = transactionType.toLowerCase();

  // CHARGES bucket: charge, payment
  if (type === 'charge' || type === 'payment') {
    return StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_CHARGES;
  }

  // REFUNDS bucket: refund, payment_refund, payment_failure_refund
  if (type === 'refund' || type === 'payment_refund' || type === 'payment_failure_refund') {
    return StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_REFUNDS;
  }

  // APPLICATION_FEE bucket: application_fee
  if (type === 'application_fee') {
    return StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_APPLICATION_FEE;
  }

  // ADJUSTMENTS bucket: adjustment
  if (type === 'adjustment') {
    return StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_ADJUSTMENTS;
  }

  return null;
}

function toCentsNumber(value: number | string | bigint | null | undefined): number {
  if (value === null || value === undefined) {
    return 0;
  }

  const numericValue = typeof value === 'bigint' ? Number(value) : Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

/**
 * Represents a single summary bucket with aggregated transaction data.
 */
export interface StripePayoutSummaryBucket {
  type: StripePayoutSummaryItemType;
  count: number;
  gross: number; // sum of amounts
  fees: number; // sum of fees
  net: number; // sum of net values
}

/**
 * Computes Stripe payout summary buckets from a flat array of transactions.
 *
 * Summary buckets:
 * - CHARGES: includes transaction types 'charge', 'payment'
 * - REFUNDS: includes 'refund', 'payment_refund', 'payment_failure_refund'
 * - APPLICATION_FEE: includes 'application_fee'
 * - ADJUSTMENTS: includes 'adjustment'
 *
 * For each transaction, it accumulates:
 * - gross: amount (sum of transaction amounts)
 * - fees: fee (sum of transaction fees)
 * - net: net (sum of transaction net values)
 * - count: incremented for each transaction in the bucket
 *
 * @param transactions - All Stripe transaction items across all pages
 * @returns Array of summary buckets in expected order: CHARGES, REFUNDS, APPLICATION_FEE, ADJUSTMENTS
 */
export function computeStripePayoutSummaryFromTransactions(
  transactions: StripeTransactionItem[] = []
): StripePayoutSummaryBucket[] {
  const reportedUnknownTypes = new Set<string>();

  // Initialize buckets for each summary type
  const buckets: Map<StripePayoutSummaryItemType, StripePayoutSummaryBucket> = new Map([
    [
      StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_CHARGES,
      {
        type: StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_CHARGES,
        count: 0,
        gross: 0,
        fees: 0,
        net: 0,
      },
    ],
    [
      StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_REFUNDS,
      {
        type: StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_REFUNDS,
        count: 0,
        gross: 0,
        fees: 0,
        net: 0,
      },
    ],
    [
      StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_APPLICATION_FEE,
      {
        type: StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_APPLICATION_FEE,
        count: 0,
        gross: 0,
        fees: 0,
        net: 0,
      },
    ],
    [
      StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_ADJUSTMENTS,
      {
        type: StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_ADJUSTMENTS,
        count: 0,
        gross: 0,
        fees: 0,
        net: 0,
      },
    ],
  ]);

  // Aggregate transactions into buckets
  transactions.forEach((transaction) => {
    const bucketType = getTransactionBucketType(transaction.transactionType);

    // Skip transactions that don't map to a bucket
    if (!bucketType) {
      const transactionType = transaction.transactionType ?? 'undefined';
      if (!reportedUnknownTypes.has(transactionType)) {
        reportedUnknownTypes.add(transactionType);
        paymentsSentry.error(
          'Unhandled Stripe payout transaction type for summary bucket mapping',
          'computeStripePayoutSummaryFromTransactions',
          `transactionType=${transactionType}`
        );
      }
      return;
    }

    const bucket = buckets.get(bucketType);
    if (!bucket) {
      paymentsSentry.error(
        'Missing Stripe payout summary bucket during aggregation',
        'computeStripePayoutSummaryFromTransactions',
        `bucketType=${bucketType}`
      );
      return;
    }

    const amount = toCentsNumber((transaction as StripeTransactionItem & { amount?: number | string | bigint }).amount);
    const fee = toCentsNumber((transaction as StripeTransactionItem & { fee?: number | string | bigint }).fee);
    const net = toCentsNumber((transaction as StripeTransactionItem & { net?: number | string | bigint }).net);

    bucket.count += 1;
    bucket.gross += amount;
    bucket.fees += fee;
    bucket.net += net;
  });

  // Return buckets in expected order: CHARGES, REFUNDS, APPLICATION_FEE, ADJUSTMENTS
  return [
    buckets.get(StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_CHARGES)!,
    buckets.get(StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_REFUNDS)!,
    buckets.get(StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_APPLICATION_FEE)!,
    buckets.get(StripePayoutSummaryItemType.PAYOUT_SUMMARY_ITEM_TYPE_ADJUSTMENTS)!,
  ];
}

/**
 * Calculates the total gross (payout total) by summing all net values from buckets.
 * @param buckets - Summary buckets
 * @returns Total gross value
 */
export function calculateTotalGrossFromBuckets(buckets: StripePayoutSummaryBucket[]): number {
  return buckets.reduce((sum, bucket) => sum + bucket.net, 0);
}
